import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSupabaseClient } from '../providers/supabase';
import { useHtClient } from '../providers/ht';


export interface Profile {
  id: string;
  created_at: string;
  first_name: string | null;
  last_name: string | null;
  title: string | null;
  firm: number | null;
  office: number | null;
  user_type: string;
  sf_account: string | null;
  email: string | null;
  info: Record<string, any> | null;
  exercises?: ExercisesData | null;
}

// Exercise interfaces
export interface ExerciseResponse {
  id: number;
  title: string;
  last_saved: string;
  responses: Record<string, any>;
}

export interface ExercisesData {
  exercises: Record<string, ExerciseResponse>;
}

export interface ExerciseUpdateInput {
  exercise_id: number;
  title: string;
  responses: Record<string, any>;
}

export interface ProfileCreateInput {
  first_name: string;
  last_name: string;
  title?: string;
  firm?: number;
  office?: number;
  user_type?: string;
}

export interface ProfileUpdateInput {
  first_name?: string;
  last_name?: string;
  title?: string;
  firm?: number;
  office?: number;
  user_type?: string;
  info?: Record<string, any>;
}

// Office count response type
export interface OfficeCounts {
  office_counts: Record<number, number>;
}

// Get all profiles with optional firm and office filters
export const useProfiles = (firm?: number, office?: number) => {
  const { data: client } = useHtClient();
  
  return useQuery({
    queryKey: ["profiles", { firm, office }],
    queryFn: async () => {
      console.log('📋 PROFILES: Fetching profiles', 
        firm ? `for firm ${firm}` : 'all',
        office ? `and office ${office}` : '');
      
      if (!client) {
        console.error('❌ PROFILES: API client not initialized');
        throw new Error("API client not initialized");
      }
      
      // Build query parameters
      const params = new URLSearchParams();
      if (firm !== undefined) params.append('firm', firm.toString());
      if (office !== undefined) params.append('office', office.toString());
      
      const url = `/profiles${params.toString() ? `?${params.toString()}` : ''}`;
      
      const startTime = performance.now();
      const response = await client.get(url);
      console.log(`⏱️ PROFILES: Fetch took ${Math.round(performance.now() - startTime)}ms`);
      
      console.log(`📦 PROFILES: Received ${response.data.length} profiles`);
      return response.data as Profile[];
    },
    staleTime: 60 * 1000, // 1 minute
  });
};

// Get current user's profile
export const useCurrentProfile = () => {
  const { data: client } = useHtClient();
  
  return useQuery({
    queryKey: ["currentProfile"],
    queryFn: async () => {
      console.log('🔍 CURRENT PROFILE: Fetching current user profile');
      
      if (!client) {
        console.error('❌ CURRENT PROFILE: API client not initialized');
        throw new Error("API client not initialized");
      }
      
      const startTime = performance.now();
      const response = await client.get('/profiles/me');
      console.log(`⏱️ CURRENT PROFILE: Fetch took ${Math.round(performance.now() - startTime)}ms`);
      
      console.log('📦 CURRENT PROFILE: Full response data:', JSON.stringify(response.data, null, 2));
      console.log(`📦 CURRENT PROFILE: Received profile data for ${response.data.first_name} ${response.data.last_name}`);
      return response.data as Profile;
    },
    staleTime: 60 * 1000, // 1 minute
    retry: (failureCount, error) => {
      // Don't retry on 401 (authentication) or 404 (not found) errors
      const status = (error as any)?.response?.status;
      if (status === 401 || status === 404) {
        console.log(`❌ CURRENT PROFILE: Not retrying on status ${status}`);
        return false;
      }
      
      // Retry up to 3 times for other errors
      if (failureCount < 3) {
        console.log(`🔄 CURRENT PROFILE: Retrying... (attempt ${failureCount + 1}/3)`);
        return true;
      }
      
      console.log('❌ CURRENT PROFILE: Max retries reached');
      return false;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
  });
};

// Get profile by email address
export const useProfileByEmail = (email: string | null | undefined) => {
  const { data: client } = useHtClient();
  
  return useQuery({
    queryKey: ["profileByEmail", email],
    queryFn: async () => {
      console.log(`🔍 PROFILE BY EMAIL: Searching for profile with email: ${email}`);
      
      if (!client) {
        console.error('❌ PROFILE BY EMAIL: API client not initialized');
        throw new Error("API client not initialized");
      }
      
      if (!email || email.trim() === '') {
        console.error('❌ PROFILE BY EMAIL: Email is required');
        throw new Error("Email is required");
      }
      
      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        console.error('❌ PROFILE BY EMAIL: Invalid email format');
        throw new Error("Invalid email format");
      }
      
      const startTime = performance.now();
      const response = await client.get(`/profiles/search/email?email=${encodeURIComponent(email)}`);
      console.log(`⏱️ PROFILE BY EMAIL: Search took ${Math.round(performance.now() - startTime)}ms`);
      
      if (response.data) {
        console.log(`📦 PROFILE BY EMAIL: Found profile for ${response.data.first_name} ${response.data.last_name}`);
        return response.data as Profile;
      } else {
        console.log(`📦 PROFILE BY EMAIL: No profile found for email: ${email}`);
        return null;
      }
    },
    enabled: !!email && email.trim() !== '', // Only run query if email is provided
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error) => {
      // Don't retry on 404 (not found) errors - that's a valid response
      const status = (error as any)?.response?.status;
      if (status === 404) {
        console.log('📭 PROFILE BY EMAIL: Profile not found (404) - not retrying');
        return false;
      }
      
      // Don't retry on 401 (authentication) errors
      if (status === 401) {
        console.log('❌ PROFILE BY EMAIL: Authentication error (401) - not retrying');
        return false;
      }
      
      // Retry up to 2 times for other errors (fewer retries for search)
      if (failureCount < 2) {
        console.log(`🔄 PROFILE BY EMAIL: Retrying... (attempt ${failureCount + 1}/2)`);
        return true;
      }
      
      console.log('❌ PROFILE BY EMAIL: Max retries reached');
      return false;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000), // Faster exponential backoff for search
  });
};

// Get specific profile by ID
export const useProfile = (profileId: string) => {
  const { data: client } = useHtClient();
  
  return useQuery({
    queryKey: ["profile", profileId],
    queryFn: async () => {
      console.log(`🔍 PROFILE: Fetching profile ${profileId}`);
      
      if (!client) {
        console.error('❌ PROFILE: API client not initialized');
        throw new Error("API client not initialized");
      }
      
      const startTime = performance.now();
      const response = await client.get(`/profiles/${profileId}`);
      console.log(`⏱️ PROFILE: Fetch took ${Math.round(performance.now() - startTime)}ms`);
      
      console.log(`📦 PROFILE: Received profile data for ${response.data.first_name} ${response.data.last_name}`);
      return response.data as Profile;
    },
    staleTime: 60 * 1000, // 1 minute
    enabled: !!profileId,
  });
};

// Create new profile
export const useCreateProfile = () => {
  const { data: client } = useHtClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (profileData: ProfileCreateInput) => {
      console.log('🆕 CREATE PROFILE: Creating new profile', `${profileData.first_name} ${profileData.last_name}`);
      
      if (!client) {
        console.error('❌ CREATE PROFILE: API client not initialized');
        throw new Error("API client not initialized");
      }
      
      const startTime = performance.now();
      const response = await client.post("/profiles", profileData);
      console.log(`⏱️ CREATE PROFILE: Request took ${Math.round(performance.now() - startTime)}ms`);
      
      console.log('✅ CREATE PROFILE: Successfully created profile', response.data.id);
      return response.data as Profile;
    },
    onSuccess: (data) => {
      console.log('🔄 CREATE PROFILE: Invalidating profile queries');
      queryClient.invalidateQueries({ queryKey: ["profiles"] });
      
      // Also invalidate firm-specific or office-specific queries if applicable
      if (data.firm) {
        queryClient.invalidateQueries({ queryKey: ["profiles", { firm: data.firm }] });
        queryClient.invalidateQueries({ queryKey: ["profilesByFirm", data.firm] });
        queryClient.invalidateQueries({ queryKey: ["officeCountsByFirm", data.firm] });
      }
      
      if (data.office) {
        queryClient.invalidateQueries({ queryKey: ["profiles", { office: data.office }] });
        queryClient.invalidateQueries({ queryKey: ["profilesByOffice", data.office] });
      }
    },
  });
};

// Update profile
export const useUpdateProfile = () => {
  const { data: client } = useHtClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ profileId, profileData }: { profileId: string; profileData: ProfileUpdateInput }) => {
      console.log(`✏️ UPDATE PROFILE: Updating profile ${profileId}`);
      
      if (!client) {
        console.error('❌ UPDATE PROFILE: API client not initialized');
        throw new Error("API client not initialized");
      }
      
      const startTime = performance.now();
      const response = await client.put(`/profiles/${profileId}`, profileData);
      console.log(`⏱️ UPDATE PROFILE: Request took ${Math.round(performance.now() - startTime)}ms`);
      
      console.log('✅ UPDATE PROFILE: Successfully updated profile', response.data.id);
      return response.data as Profile;
    },
    onSuccess: (data, variables) => {
      console.log('🔄 UPDATE PROFILE: Invalidating profile queries');
      
      // Invalidate general queries
      queryClient.invalidateQueries({ queryKey: ["profiles"] });
      queryClient.invalidateQueries({ queryKey: ["profile", variables.profileId] });
      
      // If this was the current user's profile, invalidate that too
      queryClient.invalidateQueries({ queryKey: ["currentProfile"] });
      
      // Also invalidate firm-specific or office-specific queries if applicable
      if (data.firm) {
        queryClient.invalidateQueries({ queryKey: ["profiles", { firm: data.firm }] });
        queryClient.invalidateQueries({ queryKey: ["profilesByFirm", data.firm] });
        queryClient.invalidateQueries({ queryKey: ["officeCountsByFirm", data.firm] });
      }
      
      if (data.office) {
        queryClient.invalidateQueries({ queryKey: ["profiles", { office: data.office }] });
        queryClient.invalidateQueries({ queryKey: ["profilesByOffice", data.office] });
      }
    },
  });
};

// Update current user's profile
export const useUpdateCurrentProfile = () => {
  const { data: client } = useHtClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (profileData: ProfileUpdateInput) => {
      console.log('✏️ UPDATE CURRENT PROFILE: Updating current user profile');
      
      if (!client) {
        console.error('❌ UPDATE CURRENT PROFILE: API client not initialized');
        throw new Error("API client not initialized");
      }
      
      const startTime = performance.now();
      const response = await client.put('/profiles/me', profileData);
      console.log(`⏱️ UPDATE CURRENT PROFILE: Request took ${Math.round(performance.now() - startTime)}ms`);
      
      console.log('✅ UPDATE CURRENT PROFILE: Successfully updated profile', response.data.id);
      return response.data as Profile;
    },
    onSuccess: (data) => {
      console.log('🔄 UPDATE CURRENT PROFILE: Invalidating profile queries');
      
      // Invalidate current profile query
      queryClient.invalidateQueries({ queryKey: ["currentProfile"] });
      
      // Invalidate the specific profile query
      queryClient.invalidateQueries({ queryKey: ["profile", data.id] });
      
      // Invalidate general profiles query
      queryClient.invalidateQueries({ queryKey: ["profiles"] });
      
      // Also invalidate firm-specific or office-specific queries if applicable
      if (data.firm) {
        queryClient.invalidateQueries({ queryKey: ["profiles", { firm: data.firm }] });
        queryClient.invalidateQueries({ queryKey: ["profilesByFirm", data.firm] });
        queryClient.invalidateQueries({ queryKey: ["officeCountsByFirm", data.firm] });
      }
      
      if (data.office) {
        queryClient.invalidateQueries({ queryKey: ["profiles", { office: data.office }] });
        queryClient.invalidateQueries({ queryKey: ["profilesByOffice", data.office] });
      }
    },
  });
};

// Delete profile
export const useDeleteProfile = () => {
  const { data: client } = useHtClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (profileId: string) => {
      console.log(`🗑️ DELETE PROFILE: Deleting profile ${profileId}`);
      
      if (!client) {
        console.error('❌ DELETE PROFILE: API client not initialized');
        throw new Error("API client not initialized");
      }
      
      const startTime = performance.now();
      await client.delete(`/profiles/${profileId}`);
      console.log(`⏱️ DELETE PROFILE: Request took ${Math.round(performance.now() - startTime)}ms`);
      
      console.log('✅ DELETE PROFILE: Successfully deleted profile');
      return { id: profileId };
    },
    onSuccess: (_, profileId) => {
      console.log('🔄 DELETE PROFILE: Invalidating profile queries');
      
      // Get the profile data before it's removed from the cache
      const profile = queryClient.getQueryData<Profile>(["profile", profileId]);
      
      // Invalidate the profile-specific query
      queryClient.invalidateQueries({ queryKey: ["profile", profileId] });
      
      // Invalidate general profiles query
      queryClient.invalidateQueries({ queryKey: ["profiles"] });
      
      // Also invalidate firm-specific or office-specific queries if applicable
      if (profile?.firm) {
        queryClient.invalidateQueries({ queryKey: ["profiles", { firm: profile.firm }] });
        queryClient.invalidateQueries({ queryKey: ["profilesByFirm", profile.firm] });
        queryClient.invalidateQueries({ queryKey: ["officeCountsByFirm", profile.firm] });
      }
      
      if (profile?.office) {
        queryClient.invalidateQueries({ queryKey: ["profiles", { office: profile.office }] });
        queryClient.invalidateQueries({ queryKey: ["profilesByOffice", profile.office] });
      }
    },
  });
};

// Get profiles by firm
export const useProfilesByFirm = (firmId: number) => {
  const { data: client } = useHtClient();
  
  return useQuery({
    queryKey: ["profilesByFirm", firmId],
    queryFn: async () => {
      console.log(`📋 PROFILES BY FIRM: Fetching profiles for firm ${firmId}`);
      
      if (!client) {
        console.error('❌ PROFILES BY FIRM: API client not initialized');
        throw new Error("API client not initialized");
      }
      
      const startTime = performance.now();
      const response = await client.get(`/profiles/firm/${firmId}`);
      console.log(`⏱️ PROFILES BY FIRM: Fetch took ${Math.round(performance.now() - startTime)}ms`);
      
      console.log(`📦 PROFILES BY FIRM: Received ${response.data.length} profiles`);
      return response.data as Profile[];
    },
    staleTime: 60 * 1000, // 1 minute
  });
};

// Get profiles by office
export const useProfilesByOffice = (officeId: number) => {
  const { data: client } = useHtClient();
  
  return useQuery({
    queryKey: ["profilesByOffice", officeId],
    queryFn: async () => {
      console.log(`📋 PROFILES BY OFFICE: Fetching profiles for office ${officeId}`);
      
      if (!client) {
        console.error('❌ PROFILES BY OFFICE: API client not initialized');
        throw new Error("API client not initialized");
      }
      
      const startTime = performance.now();
      const response = await client.get(`/profiles/office/${officeId}`);
      console.log(`⏱️ PROFILES BY OFFICE: Fetch took ${Math.round(performance.now() - startTime)}ms`);
      
      console.log(`📦 PROFILES BY OFFICE: Received ${response.data.length} profiles`);
      return response.data as Profile[];
    },
    staleTime: 60 * 1000, // 1 minute
  });
};

// Get office counts by firm
export const useOfficeCountsByFirm = (firmId: number) => {
  const { data: client } = useHtClient();
  
  return useQuery({
    queryKey: ["officeCountsByFirm", firmId],
    queryFn: async () => {
      console.log(`📊 OFFICE COUNTS: Fetching office counts for firm ${firmId}`);
      
      if (!client) {
        console.error('❌ OFFICE COUNTS: API client not initialized');
        throw new Error("API client not initialized");
      }
      
      const startTime = performance.now();
      const response = await client.get(`/profiles/firm/${firmId}/office-counts`);
      console.log(`⏱️ OFFICE COUNTS: Fetch took ${Math.round(performance.now() - startTime)}ms`);
      
      console.log(`📦 OFFICE COUNTS: Received counts for ${Object.keys(response.data.office_counts).length} offices`);
      return response.data as OfficeCounts;
    },
    staleTime: 60 * 1000, // 1 minute
  });
};

// Create profile for email
export const useCreateProfileForEmail = () => {
  const { data: client } = useHtClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ 
      email, 
      profile 
    }: { 
      email: string; 
      profile: ProfileCreateInput;
    }) => {
      console.log('🆕 CREATE PROFILE FOR EMAIL: Creating profile for user:', email);
      console.log('🆕 CREATE PROFILE FOR EMAIL: Profile data:', profile);
      
      if (!client) {
        console.error('❌ CREATE PROFILE FOR EMAIL: API client not initialized');
        throw new Error("API client not initialized");
      }
      
      const startTime = performance.now();
      const response = await client.post(
        `/profiles/create-for-email?email=${encodeURIComponent(email)}`,
        profile
      );
      console.log(`⏱️ CREATE PROFILE FOR EMAIL: Request took ${Math.round(performance.now() - startTime)}ms`);
      
      console.log('✅ CREATE PROFILE FOR EMAIL: Successfully created profile', response.data.id);
      return response.data as Profile;
    },
    onSuccess: (data) => {
      console.log('🔄 CREATE PROFILE FOR EMAIL: Invalidating profile queries');
      
      // Invalidate general queries
      queryClient.invalidateQueries({ queryKey: ["profiles"] });
      
      // Also invalidate firm-specific or office-specific queries if applicable
      if (data.firm) {
        queryClient.invalidateQueries({ queryKey: ["profiles", { firm: data.firm }] });
        queryClient.invalidateQueries({ queryKey: ["profilesByFirm", data.firm] });
        queryClient.invalidateQueries({ queryKey: ["officeCountsByFirm", data.firm] });
      }
      
      if (data.office) {
        queryClient.invalidateQueries({ queryKey: ["profiles", { office: data.office }] });
        queryClient.invalidateQueries({ queryKey: ["profilesByOffice", data.office] });
      }
    }
  });
}; 

// Exercise hooks

// Get current user's exercises
export const useCurrentUserExercises = () => {
  const { data: client } = useHtClient();
  
  return useQuery({
    queryKey: ["currentUserExercises"],
    queryFn: async () => {
      console.log('🔍 EXERCISES: Fetching current user exercises');
      
      if (!client) {
        console.error('❌ EXERCISES: API client not initialized');
        throw new Error("API client not initialized");
      }
      
      const startTime = performance.now();
      const response = await client.get('/profiles/me/exercises');
      console.log(`⏱️ EXERCISES: Fetch took ${Math.round(performance.now() - startTime)}ms`);
      
      console.log('📦 EXERCISES: Received exercises data');
      return response.data as ExercisesData;
    },
    staleTime: 30 * 1000, // 30 seconds (shorter since this changes frequently)
  });
};

// Auto-save exercise
export const useAutoSaveExercise = () => {
  const { data: client } = useHtClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (exerciseData: ExerciseUpdateInput) => {
      console.log(`💾 AUTO-SAVE: Saving exercise ${exerciseData.exercise_id}`);
      
      if (!client) {
        console.error('❌ AUTO-SAVE: API client not initialized');
        throw new Error("API client not initialized");
      }
      
      const startTime = performance.now();
      const response = await client.put(`/profiles/me/exercises/${exerciseData.exercise_id}`, exerciseData);
      console.log(`⏱️ AUTO-SAVE: Request took ${Math.round(performance.now() - startTime)}ms`);
      
      console.log('✅ AUTO-SAVE: Successfully saved exercise');
      return response.data;
    },
    onSuccess: () => {
      console.log('🔄 AUTO-SAVE: Invalidating exercises query');
      queryClient.invalidateQueries({ queryKey: ["currentUserExercises"] });
    },
    // Don't show error notifications for auto-save failures - just log them
    onError: (error) => {
      console.error('❌ AUTO-SAVE: Failed to save exercise:', error);
    }
  });
};


export interface MyAdvisor {
  name: string | null;
  email: string | null;
  firm_name: string | null;
}

// The firm advisor a logged-in quiz taker came through, or null for walk-in quiz
// takers (McKayla 2026-10-12: "Connect With an Advisor" goes to that firm's advisor).
export const useMyAdvisor = (enabled: boolean) => {
  const { data: client } = useHtClient();

  return useQuery({
    queryKey: ["myAdvisor"],
    queryFn: async () => {
      if (!client) throw new Error("API client not initialized");
      const response = await client.get('/profiles/me/advisor');
      return (response.data?.advisor ?? null) as MyAdvisor | null;
    },
    enabled: enabled && !!client,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
};
